import React, { useRef } from "react";
import { Users, ChevronLeft, ChevronRight, ArrowRight, Video, Zap, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { Skeleton } from "@/components/ui/skeleton";
import { useTopCoaches } from "@/hooks/use-marketplace-search";
import { usePopularGroupClasses, usePopularCrashCourses } from "@/hooks/use-offering-search";
import { OfferingCard } from "@/components/offerings/OfferingCard";

/* ───── Data ───── */
const AVATAR_GRADIENTS = [
  "from-violet-500 to-indigo-600",
  "from-sky-500 to-amber-600",
  "from-emerald-500 to-amber-600",
  "from-orange-500 to-rose-500",
  "from-pink-500 to-fuchsia-600",
];

function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash);
}

function getAvatarGradient(seed: string): string {
  return AVATAR_GRADIENTS[hashString(seed) % AVATAR_GRADIENTS.length];
}

function getInitials(name: string): string {
  return name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

const OFFERING_TYPE_LABELS: Record<string, string> = {
  ONE_TO_ONE: "1-on-1",
  GROUP: "Group",
  CRASH_COURSE: "Crash Course",
};

const OFFERING_TYPE_ICONS: Record<string, React.ReactNode> = {
  ONE_TO_ONE: <Video className="w-3 h-3" />,
  GROUP: <Users className="w-3 h-3" />,
  CRASH_COURSE: <Zap className="w-3 h-3" />,
};

const OFFERING_TYPE_COLORS: Record<string, { color: string; background: string }> = {
  ONE_TO_ONE:   { color: "#2F6BD8", background: "rgba(47,107,216,0.1)" },
  GROUP:        { color: "#0E8A78", background: "rgba(14,138,120,0.1)" },
  CRASH_COURSE: { color: "#D9472B", background: "rgba(217,71,43,0.1)"  },
};

const EXPERIENCE_LABELS: Record<string, string> = {
  LESS_THAN_1: "< 1 yr exp",
  ONE_TO_3: "1–3 yrs exp",
  THREE_TO_5: "3–5 yrs exp",
  FIVE_TO_10: "5–10 yrs exp",
  MORE_THAN_10: "10+ yrs exp",
};

/* ───── Horizontal Gallery ───── */
function Gallery({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
  };
  return (
    <div className="relative group">
      <button onClick={() => scroll("left")} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 rounded-full bg-white border border-[#DCDDEE] shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer">
        <ChevronLeft className="w-4 h-4 text-[#262A7A]" />
      </button>
      <div ref={scrollRef} className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2">
        {children}
      </div>
      <button onClick={() => scroll("right")} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 rounded-full bg-white border border-[#DCDDEE] shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer">
        <ChevronRight className="w-4 h-4 text-[#262A7A]" />
      </button>
    </div>
  );
}

/* ───── Section Header ───── */
function SectionHeader({ title, subtitle, cta }: { title: string; subtitle: string; cta?: { label: string; to: string } }) {
  return (
    <div className="flex items-end justify-between mb-8 gap-4">
      <div>
        <h2 className="font-sans text-2xl sm:text-3xl font-semibold text-[#262A7A] mb-1">{title}</h2>
        <p className="text-[#5A5E80] text-sm">{subtitle}</p>
      </div>
      {cta && (
        <Link to={cta.to} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#262A7A] hover:text-[#363A96] whitespace-nowrap flex-shrink-0 transition-colors">
          {cta.label} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

/* ───── Load More Button ───── */
function LoadMoreBtn({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="flex-shrink-0 w-36 rounded-2xl border border-dashed border-[#DCDDEE] bg-white flex flex-col items-center justify-center gap-2 text-sm font-medium text-[#5A5E80] hover:text-[#262A7A] hover:border-[#A9ACD6] transition-colors cursor-pointer disabled:cursor-default"
    >
      {loading
        ? <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        : <ArrowRight className="w-5 h-5" />}
      {loading ? "Loading…" : "Load more"}
    </button>
  );
}

/* ───── Top Coaches ───── */
function TopCoachesSection() {
  const { t } = useLanguage();
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useTopCoaches(8);
  const teachers = data?.pages.flatMap((p) => p.content) ?? [];

  if (isError) return null;
  if (!isLoading && teachers.length === 0) return null;

  return (
    <div className="py-14 sm:py-20">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeader
          title={t("proof.topCoaches")}
          subtitle={t("proof.topCoaches.subtitle")}
          cta={{ label: "Browse all teachers", to: "/search/one-to-one" }}
        />
        <Gallery>
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-64 rounded-2xl border border-[#DCDDEE] bg-white overflow-hidden">
                  <Skeleton className="h-44 w-full" />
                  <div className="p-4 space-y-2.5">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))
            : teachers.map((teacher) => (
                <Link
                  to={`/t/${teacher.handle ?? teacher.teacherProfileId}`}
                  key={teacher.teacherProfileId}
                  className="flex-shrink-0 w-64 rounded-2xl border border-[#DCDDEE] bg-white overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group flex flex-col"
                >
                  <div className="relative h-44 overflow-hidden">
                    {teacher.profilePictureUrl ? (
                      <img
                        src={teacher.profilePictureUrl}
                        alt={teacher.fullName}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className={cn("w-full h-full bg-gradient-to-br flex items-center justify-center", getAvatarGradient(teacher.fullName))}>
                        <span className="text-white font-bold text-4xl">{getInitials(teacher.fullName)}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0E8A78] text-white text-[10px] font-bold uppercase tracking-wide">
                      <Sparkles className="w-2.5 h-2.5" /> Verified
                    </span>
                  </div>

                  <div className="p-4 flex flex-col flex-1">
                    <p className="font-semibold text-[#262A7A] text-base leading-snug group-hover:text-[#363A96] transition-colors mb-2">
                      {teacher.fullName}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {teacher.offeringTypes.map((type) => {
                        const c = OFFERING_TYPE_COLORS[type] ?? OFFERING_TYPE_COLORS.ONE_TO_ONE;
                        return (
                          <span
                            key={type}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                            style={{ color: c.color, background: c.background }}
                          >
                            {OFFERING_TYPE_ICONS[type]}
                            {OFFERING_TYPE_LABELS[type] ?? type}
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-xs text-[#A9ACD6] mt-auto pt-3 border-t border-[#DCDDEE]">
                      {teacher.yearsTeachingExperience
                        ? EXPERIENCE_LABELS[teacher.yearsTeachingExperience] ?? teacher.yearsTeachingExperience
                        : "Experience not listed"}
                    </p>
                  </div>
                </Link>
              ))}
          {hasNextPage && <LoadMoreBtn onClick={() => fetchNextPage()} loading={isFetchingNextPage} />}
        </Gallery>
      </div>
    </div>
  );
}

/* ───── Group Classes ───── */
function GroupClassesSection() {
  const { t } = useLanguage();
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = usePopularGroupClasses();
  const classes = data?.pages.flatMap((p) => p.content) ?? [];

  if (isError) return null;
  if (!isLoading && classes.length === 0) return null;

  return (
    <div className="py-14 sm:py-20 bg-[hsl(237,52%,97%)]">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeader
          title={t("proof.groupClasses")}
          subtitle={t("proof.groupClasses.subtitle")}
          cta={{ label: "Browse group classes", to: "/search/group" }}
        />
        <Gallery>
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-64 rounded-2xl border border-[#DCDDEE] bg-white overflow-hidden">
                  <Skeleton className="h-36 w-full" />
                  <div className="p-4 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/3" /></div>
                </div>
              ))
            : classes.map((o) => <OfferingCard key={o.offeringId} offering={o} className="w-64 shrink-0" />)}
          {hasNextPage && <LoadMoreBtn onClick={() => fetchNextPage()} loading={isFetchingNextPage} />}
        </Gallery>
      </div>
    </div>
  );
}

/* ───── Crash Courses ───── */
function CrashCoursesSection() {
  const { t } = useLanguage();
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = usePopularCrashCourses();
  const courses = data?.pages.flatMap((p) => p.content) ?? [];

  if (isError) return null;
  if (!isLoading && courses.length === 0) return null;

  return (
    <div className="py-14 sm:py-20">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeader
          title={t("proof.crashCourses")}
          subtitle={t("proof.crashCourses.subtitle")}
          cta={{ label: "Browse crash courses", to: "/search/crash-course" }}
        />
        <Gallery>
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-64 rounded-2xl border border-[#DCDDEE] bg-white overflow-hidden">
                  <Skeleton className="h-36 w-full" />
                  <div className="p-4 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/3" /></div>
                </div>
              ))
            : courses.map((o) => <OfferingCard key={o.offeringId} offering={o} className="w-64 shrink-0" />)}
          {hasNextPage && <LoadMoreBtn onClick={() => fetchNextPage()} loading={isFetchingNextPage} />}
        </Gallery>
      </div>
    </div>
  );
}

/* ───── Main Export ───── */
export function SocialProofSection() {
  return (
    <section>
      <TopCoachesSection />
      <GroupClassesSection />
      <CrashCoursesSection />
    </section>
  );
}
